import { useMemo } from "react";
import { format } from "date-fns";
import {
  CmaModelChartData,
  SimulationJobSummaryStatistics,
  useAssetClassesAssetClassStatisticViewRetrieveQuery,
  useSimulationSimulationJobGetSummaryStatisticsListQuery,
} from "@app/api/carApi.generated";
import { ExcelDataFormat, ExcelTable } from "@app/hooks/useExcelExport";
import {
  AssetClass,
  AssetClassLoadType,
  useAssetClasses,
} from "@app/features/assets/useAssetClasses";
import { AssetClassStatisticViewItemResponse } from "@app/pages/assetAllocation/editPortfolioGroup/useAssetClassStatistics";
import { formatDate, isDefined } from "@app/utils";
import { CarStatBoxItem } from "@app/components/StatBox";
import { Theme, useTheme } from "@mui/material";
import { pendoClasses } from "@app/core/thirdParty/pendo";
import { useUserAccess } from "@app/hooks/useUserAccess";

export interface CarAssetClassDftItem extends AssetClass {
  modelChartData: CmaModelChartData;
  startDate: string;
  statItems: CarStatBoxItem[];
  realStaticExpectedReturn: number | null;
}

export interface CarAssetClassAnalysisItem extends AssetClass {
  dft: number | null;
  realStaticExpectedReturn: number | null;
  realPriceDrivenExpectedReturn: number | null;
  nominalStaticExpectedReturn: number | null;
  nominalPriceDrivenExpectedReturn: number | null;
  volatility: number | null;
  alpha: number | null;
  beta: number | null;

  fixedIncomeDuration: number | null;
  fixedIncomeConvexity: number | null;
  currentEquityDividendYield: number | null;
  currentFixedIncomeYield: number | null;
  dftItem?: CarAssetClassDftItem;
}

export type CarAssetClassAnalysisItemKey = keyof Pick<
  CarAssetClassAnalysisItem,
  | "dft"
  | "nominalPriceDrivenExpectedReturn"
  | "nominalStaticExpectedReturn"
  | "realPriceDrivenExpectedReturn"
  | "realStaticExpectedReturn"
  | "volatility"
  | "alpha"
  | "beta"
  | "fixedIncomeDuration"
  | "fixedIncomeConvexity"
  | "currentEquityDividendYield"
  | "currentFixedIncomeYield"
>;

export const getDistanceFromTrendExcelTable = (params: {
  name: string;
  item: CarAssetClassDftItem;
}): ExcelTable => {
  const data = params.item.modelChartData;

  return {
    name: params.name,
    columns: [
      {
        label: "",
        format: ExcelDataFormat.general,
        charWidth: 15,
      },
      {
        label: "Trend Line",
        format: ExcelDataFormat.percent,
        charWidth: 15,
      },
      {
        label: "Historic Line",
        format: ExcelDataFormat.percent,
        charWidth: 15,
      },
    ],
    rows:
      data?.trend_x?.map((i, idx) => [
        format(new Date(i), "MMM yyyy"),
        (data.trend_y_trend_line?.[idx] ?? 0) / 100,
        (data.trend_y_historic_line?.[idx] ?? 0) / 100,
      ]) ?? [],
  };
};

export const getDftItem = ({
  theme,
  assetClass,
  summaryStatItem,
}: {
  theme: Theme;
  assetClass: AssetClass;
  summaryStatItem: SimulationJobSummaryStatistics;
}): CarAssetClassDftItem | undefined => {
  const firstDataPoint = summaryStatItem?.model_chart_data?.trend_x?.at(0);
  const startingDft =
    (summaryStatItem?.model_chart_data?.boundary_scatter_x?.at(0) ?? 0) / 100;

  if (!summaryStatItem.model_chart_data) {
    return undefined;
  }

  return {
    ...assetClass,
    modelChartData: summaryStatItem.model_chart_data,
    startDate: firstDataPoint ? formatDate(firstDataPoint) : "-",
    realStaticExpectedReturn:
      summaryStatItem.real_static_expected_return ?? null,
    statItems: [
      {
        label: "STARTING DFT",
        pendoClass: pendoClasses.assetClassAnalysisDftStartingDft,
        value: startingDft,
        format: ExcelDataFormat.percent,
      },
      {
        label: "ENDING DFT",
        pendoClass: pendoClasses.assetClassAnalysisDftEndingDft,
        value: (summaryStatItem?.model_chart_data?.label_end_dft ?? 0) / 100,

        format: ExcelDataFormat.percent,
      },
      {
        label: "PRICE-DRIVEN\nEXPECTED RETURN",
        pdfLabel: "PRICE-DRIVEN E(R)",
        pendoClass: pendoClasses.assetClassAnalysisDftPriceDrivenExpectedReturn,
        value:
          (summaryStatItem?.model_chart_data?.label_expected_return ?? 0) / 100,
        format: ExcelDataFormat.percent,
      },
      {
        label: "STATIC EXPECTED\nRETURN TREND LINE SLOPE",
        pdfLabel: "E(R) TREND",
        pendoClass:
          pendoClasses.assetClassAnalysisDftStaticExpectedReturnTrendLineSlope,
        legendColor: theme.palette.caravelOrangePrimary,
        valueColor: theme.palette.caravelOrangePrimary,
        value: summaryStatItem?.real_static_expected_return ?? 0,
        format: ExcelDataFormat.percent,
      },
      {
        label: "HISTORIC LINE",
        pendoClass: pendoClasses.assetClassAnalysisDftHistoricLine,
        hideValue: true,
        legendColor: theme.palette.caravelBlueSecondary,
      },
    ],
  };
};

const sliceDftItem = (
  startIndex: number,
  dftItem: CarAssetClassDftItem,
): CarAssetClassDftItem => {
  if (startIndex < 0) {
    startIndex = 0;
  }

  const slicedModel: CmaModelChartData = {
    ...dftItem.modelChartData,
    trend_x: dftItem.modelChartData.trend_x?.slice(startIndex),
    trend_y_trend_line:
      dftItem.modelChartData.trend_y_trend_line?.slice(startIndex),
    trend_y_historic_line:
      dftItem.modelChartData.trend_y_historic_line?.slice(startIndex),
    boundary_x: dftItem.modelChartData.boundary_x?.slice(startIndex),
    boundary_y_upper:
      dftItem.modelChartData.boundary_y_upper?.slice(startIndex),
    boundary_y_expected_ret:
      dftItem.modelChartData.boundary_y_expected_ret?.slice(startIndex),
    boundary_y_lower:
      dftItem.modelChartData.boundary_y_lower?.slice(startIndex),
    boundary_scatter_x:
      dftItem.modelChartData.boundary_scatter_x?.slice(startIndex),
    boundary_scatter_y:
      dftItem.modelChartData.boundary_scatter_y?.slice(startIndex),
  };

  const firstDataPoint = slicedModel?.trend_x?.at(0);
  const startingDft = (slicedModel?.boundary_scatter_x?.at(0) ?? 0) / 100;

  const result: CarAssetClassDftItem = {
    ...dftItem,
    startDate: firstDataPoint ? formatDate(firstDataPoint) : "-",
    modelChartData: slicedModel,
    statItems: dftItem.statItems.map((i, idx) =>
      idx === 0 ? { ...i, value: startingDft } : i,
    ), // todo better method than find by index ??? idx === 0 - STARTING DFT
  };

  return result;
};

export const useAssetClassSummaryStat = (props: {
  type: AssetClassLoadType;
  simulationJobId?: string;
  filterByUsableInPortfolios?: boolean;
}) => {
  const userAccess = useUserAccess();
  const assetClasses = useAssetClasses({
    type: props.type,
    simulationJobId: props.simulationJobId,
    filterByUsableInPortfolios: props.filterByUsableInPortfolios,
    filter: (ac) => userAccess.isCaravelUser || ac.isEquity,
  });

  const theme = useTheme();

  const summaryStat = useSimulationSimulationJobGetSummaryStatisticsListQuery(
    {
      id: assetClasses.simulationJobId ?? "",
    },
    {
      skip: !assetClasses.simulationJobId,
    },
  );

  const acStatData = useAssetClassesAssetClassStatisticViewRetrieveQuery();

  const acStatDataResponse = useMemo(
    () => acStatData.data as AssetClassStatisticViewItemResponse | undefined,
    [acStatData.data],
  );

  return useMemo(() => {
    const isLoading =
      assetClasses.isLoading || acStatData.isLoading || summaryStat.isLoading;

    const dftItems = assetClasses.items
      .map<CarAssetClassDftItem | undefined>((ac) => {
        const summaryStatItem = summaryStat.data?.find(
          (s) => s.asset_class4 === ac.level4Id,
        );

        return summaryStatItem?.model_chart_data
          ? getDftItem({ theme, assetClass: ac, summaryStatItem })
          : undefined;
      })
      .filter(isDefined);

    const acSummaryItems = assetClasses.items
      .map<CarAssetClassAnalysisItem | undefined>((ac) => {
        const summaryStatItem = summaryStat.data?.find(
          (s) => s.asset_class4 === ac.level4Id,
        );

        const statDataItem = acStatDataResponse?.[ac.level4Id];

        const result: CarAssetClassAnalysisItem | undefined = summaryStatItem
          ? {
              ...ac,
              dftItem: dftItems.find((i) => i.level4Id === ac.level4Id),
              dft: summaryStatItem.dft ?? summaryStatItem.current_yield ?? null,
              realStaticExpectedReturn:
                summaryStatItem.real_static_expected_return ?? null,
              realPriceDrivenExpectedReturn:
                summaryStatItem.real_dynamic_expected_return ?? null,
              nominalStaticExpectedReturn:
                summaryStatItem.nominal_static_expected_return ?? null,
              nominalPriceDrivenExpectedReturn:
                summaryStatItem.nominal_dynamic_expected_return ?? null,
              volatility: summaryStatItem.volatility ?? null,
              alpha: summaryStatItem.alpha ?? null,
              beta: summaryStatItem.beta ?? null,

              fixedIncomeDuration: statDataItem?.duration ?? null,
              fixedIncomeConvexity: statDataItem?.convexity ?? null,
              currentEquityDividendYield: statDataItem?.equity_yield ?? null,
              currentFixedIncomeYield: statDataItem?.yield_to_worst ?? null,
            }
          : undefined;
        return result;
      })
      .filter(isDefined);

    const getDftItemsForCompare = (
      assetClass1Id?: string,
      assetClass2Id?: string,
    ) => {
      const item1 = dftItems.find((i) => i.level4Id === assetClass1Id);

      const item2 = dftItems.find((i) => i.level4Id === assetClass2Id);

      if (item1 && item2) {
        const item1XStartValue = item1.modelChartData.trend_x?.at(0);

        const item2XStartValue = item2.modelChartData.trend_x?.at(0);

        const item1XIndex =
          item1.modelChartData.trend_x?.findIndex(
            (i) => i === item2XStartValue,
          ) ?? -1;

        const item2XIndex =
          item2.modelChartData.trend_x?.findIndex(
            (i) => i === item1XStartValue,
          ) ?? -1;

        return {
          item1: sliceDftItem(item1XIndex, item1),
          item2: sliceDftItem(item2XIndex, item2),
        };
      } else {
        return {};
      }
    };

    return {
      isLoading,
      acSummaryItems,
      dftItems,
      getDftItemsForCompare,
    };
  }, [
    theme,
    acStatData.isLoading,
    acStatDataResponse,
    assetClasses,
    summaryStat.data,
    summaryStat.isLoading,
  ]);
};
